import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import removeImageFromLibraryOperation from 'editor/src/store/editorModules/customerLibrary/operation/removeImageFromLibraryOperation';
import retryLibraryRequestOperation from 'editor/src/store/editorModules/customerLibrary/operation/retryLibraryRequestOperation';
import saveImageToLibraryOperation from 'editor/src/store/editorModules/customerLibrary/operation/saveImageToLibraryOperation';
import {
  getLibraryRequest,
  isImageSaved,
  isLibraryEnabled,
} from 'editor/src/store/editorModules/customerLibrary/selector';
import getImageById from 'editor/src/store/gallery/selector/getImageById';
import { ImageState } from 'editor/src/store/gallery/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import Button from 'editor/src/component/Button';
import IconCheck from 'editor/src/component/Icon/IconCheck';
import IconMinus from 'editor/src/component/Icon/IconMinus';
import IconPlus from 'editor/src/component/Icon/IconPlus';
import IconRefresh from 'editor/src/component/Icon/IconRefresh';

import styles from './index.module.scss';

interface Props {
  imageId: string;
  /** In the My library tab the item removes the image from the library instead of saving it. */
  inLibraryTab?: boolean;
}

function LibraryMenuItem({ imageId, inLibraryTab }: Props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { enabled, saved, request, savable } = useSelector((state) => {
    const image = getImageById(state, imageId);
    return {
      enabled: isLibraryEnabled(state),
      saved: isImageSaved(state, imageId),
      request: getLibraryRequest(state, imageId),
      savable: image?.state === ImageState.UPLOADED && image.savableToLibrary === true,
    };
  }, shallowEqual);

  if (!enabled) {
    return null;
  }

  const removing = request?.saved === false;
  const saving = request?.saved === true;

  if (inLibraryTab) {
    if (removing && request?.status === 'pending') {
      return <Item label={t('editor-library-removing')} disabled className="cy-library-removing" />;
    }
    if (removing && request?.status === 'failed') {
      return (
        <Item
          label={t('editor-library-retry-remove')}
          icon={<IconRefresh />}
          className="cy-library-retry-remove"
          onClick={() => dispatch(retryLibraryRequestOperation(imageId))}
        />
      );
    }
    return (
      <Item
        label={t('editor-library-remove')}
        icon={<IconMinus />}
        className="cy-library-remove"
        onClick={() => dispatch(removeImageFromLibraryOperation(imageId))}
      />
    );
  }

  if (saved) {
    if (removing && request?.status === 'pending') {
      return <Item label={t('editor-library-removing')} disabled className="cy-library-removing" />;
    }
    return (
      <Item
        label={t('editor-library-saved')}
        icon={<IconCheck className={styles.savedIcon} />}
        className="cy-library-saved"
        readOnly
      />
    );
  }
  if (saving && request?.status === 'pending') {
    return <Item label={t('editor-library-saving')} disabled className="cy-library-saving" />;
  }
  if (saving && request?.status === 'failed') {
    return (
      <Item
        label={t('editor-library-retry-save')}
        icon={<IconRefresh />}
        className="cy-library-retry-save"
        onClick={() => dispatch(retryLibraryRequestOperation(imageId))}
      />
    );
  }
  // uploads in flight have no asset id yet, and the merchant's template images are not the customer's to keep
  if (!savable) {
    return null;
  }
  return (
    <Item
      label={t('editor-library-save')}
      icon={<IconPlus />}
      className="cy-library-save"
      onClick={() => dispatch(saveImageToLibraryOperation(imageId))}
    />
  );
}

interface ItemProps {
  label: string;
  className: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  /** A confirmation row: same layout as the actions, announced as disabled, nothing to click. */
  readOnly?: boolean;
  onClick?: () => void;
}

function Item({ label, className, icon, disabled, readOnly, onClick }: ItemProps) {
  return (
    <div className={styles.itemWrap}>
      <Button
        secondary
        className={cn(styles.item, className, { [styles.disabled]: disabled, [styles.readOnly]: readOnly })}
        noPadding
        disabled={disabled}
        ariaDisabled={readOnly}
        onClick={onClick}
        stopPropagation
      >
        {icon}
        {label}
      </Button>
    </div>
  );
}

export default React.memo(LibraryMenuItem);
